import { KeyboardEvent } from "react";
import { formatCurrency } from "shared/utils";

// Every payroll amount column is a plain Postgres INT4 (32-bit signed) — see
// the matching POSTGRES_INT4_MAX in spott-server/src/services/payroll/
// shared.ts. Capping the input here gives an inline "value out of range"
// affordance instead of only finding out after a round trip fails.
const POSTGRES_INT4_MAX = 2147483647;

// AntD's InputNumber only restricts typed keystrokes to digits when it has
// NO custom `formatter` — supplying one (to render "Rp 1.000" instead of a
// bare number) makes it treat the field as free text, so without this guard
// a letter types right in and just sits there until blur/parse silently
// drops it to 0. Block anything that isn't a plain digit; let every
// modifier-held combo (copy/paste/select-all) and multi-char key name
// (Backspace, Delete, ArrowLeft, Tab, etc.) through unconditionally, since
// there's never a legitimate reason for a bare letter to land in a Rupiah
// amount field.
const blockNonDigitKeys = (e: KeyboardEvent<HTMLInputElement>) => {
  if (e.ctrlKey || e.metaKey || e.altKey) return;
  if (e.key.length > 1) return;
  if (!/[0-9]/.test(e.key)) e.preventDefault();
};

// Same formatter/parser pair as the one existing money InputNumber in the
// app (products/package-detail), pulled out here since payroll needs it
// across several fields (rates, tiers, salary, recurring items).
export const currencyInputProps = {
  style: { width: "100%" } as const,
  // `value` can arrive as the STRING "0" (AntD stringifies for the
  // formatter callback), and "0" is truthy in JS — a plain `value ? ... :
  // ""` check let a cleared field (parser below always returns 0 for empty
  // input, never null) render the literal text "Rp 0" instead of falling
  // back to the placeholder. Comparing the numeric value against 0 instead
  // treats both `0` and `"0"` as "no amount entered".
  formatter: (value?: number | string) => (value && Number(value) > 0 ? formatCurrency(Number(value)) : ""),
  parser: (value?: string) => (value ? parseInt(value.replace(/[Rp,.]/g, ""), 10) || 0 : 0),
  max: POSTGRES_INT4_MAX,
  onKeyDown: blockNonDigitKeys,
  // No +/- stepper — a Rupiah amount is typed or pasted in whole, never
  // incremented one rupiah at a time.
  controls: false,
};


// The rate boxes on the class commission page must tell "blank" (not filled,
// refused on Simpan) apart from "Rp 0" (a deliberate zero, allowed). The
// shared props above hide 0 as blank, which suits Gaji Pokok and item amounts
// but made a typed zero indistinguishable from an untouched box there. An
// emptied box parses to "" so InputNumber reports null, never 0.
export const rateAmountInputProps = {
  ...currencyInputProps,
  formatter: (value?: number | string) =>
    value === undefined || value === null || value === "" ? "" : formatCurrency(Number(value)),
  parser: (value?: string) => {
    const digits = (value ?? "").replace(/\D/g, "");
    return digits === "" ? "" : parseInt(digits, 10);
  },
};
